import React, { useState } from 'react';
import { Box, Divider, Paper, Typography } from '@mui/material';

import { CreateInsuranceInput, getArchiveReasonLabel, insuranceManualVerification } from '@uplift/api';
import { useL10n } from '@uplift/hooks';
import { DataGrid } from '@mui/x-data-grid';
import { CalendarDateService } from '@uplift/services';
import { makeStyles } from 'tss-react/mui';
import { getDateFormattedForDB, MoreOptionsDropdown, MoreOptionsDropdownItem, Tooltip, useSaveInsurance } from '../../..';
import { ArchivedInsuranceDetailsModal } from './archived-insurance-details-modal';
import { ArchivedInsuranceRestoreModal } from './archived-insurance-restore-modal';
import { InsuranceDataType } from '../Settings/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getManualVerificationInputFromBenefitsFormValues, InsuranceBenefitsFormType } from '../hooks/use-benefits-form.hook';


const getArchivedAtDisplay = (archivedAt: string) => {
  const date = CalendarDateService.date(archivedAt).tz(CalendarDateService.timezones.ET);

  if (date.isBefore(CalendarDateService.now(CalendarDateService.timezones.ET).subtract(1, 'week'))) {
    return date.format('M/D/YYYY, h:mm A');
  }

  return date.fromNow();
};

export type ArchivedInsurancesProps = {
  userId: string;
  primaryInsurance?: InsuranceDataType | null;
  secondaryInsurance?: InsuranceDataType | null;
  archivedInsurances?: InsuranceDataType[];
};

export const ArchivedInsurances: React.VFC<ArchivedInsurancesProps> = ({ userId, primaryInsurance, secondaryInsurance, archivedInsurances }) => {
  const queryClient = useQueryClient();
  const { saveInsurance } = useSaveInsurance(userId);
  const restoreDisabledReason = primaryInsurance && secondaryInsurance ? 'Can’t restore with active primary and secondary insurance' : null;
  // if primary insurance exists, restore insurance as secondary
  const isRestoreAsSecondary = !!primaryInsurance;
  const restoreMutation = useMutation({
    mutationFn: async ({
      archivedInsurance,
      benefits
    }: {
      archivedInsurance: InsuranceDataType;
      benefits: InsuranceBenefitsFormType;
    }) => {
      if (restoreDisabledReason) {
        // guard clause, do nothing
        return;
      }

      const isSecondaryInsurance = isRestoreAsSecondary;

      const insurance: CreateInsuranceInput = {
        isPrimary: archivedInsurance.isPrimary,
        companyId: archivedInsurance.companyId,
        groupNumber: archivedInsurance.groupNumber,
        subscriberId: archivedInsurance.subscriberId,
        cardFrontAssetId: archivedInsurance.insuranceCardFront,
        cardBackAssetId: archivedInsurance.insuranceCardBack,
        firstName: archivedInsurance.firstName,
        lastName: archivedInsurance.lastName,
        dob: getDateFormattedForDB(archivedInsurance.birthDate),
        address: {
          line1: archivedInsurance.address1,
          line2: archivedInsurance.address2 ?? '',
          city: archivedInsurance.city,
          stateId: archivedInsurance.state,
          zip: archivedInsurance.zip,
        },
        isSecondaryInsurance: isSecondaryInsurance,
        patientId: userId,
      };

      const newInsurance = await saveInsurance(insurance);

      if (!newInsurance) {
        throw new Error('Failed to restore insurance');
      }

      if (!isSecondaryInsurance) {
        await insuranceManualVerification({
          insuranceId: newInsurance.id,
          ...getManualVerificationInputFromBenefitsFormValues(benefits),
        });
      }

      await queryClient.invalidateQueries([ 'insurances', userId ]);
    }
  });
  const { classes } = useStyles();
  const l10n = useL10n();


  return (
    <Box mt={4}>
      <Typography variant="h4" pb={2}>{l10n.accountSettings.insurance.archivedInsurance}</Typography>
      <Paper elevation={4}>
        <DataGrid
          rows={archivedInsurances ?? []}
          columns={[
            {
              field: 'companyName',
              headerName: 'Insurance Provider',
              width: 347,
              sortable: false
            },
            {
              field: 'subscriberId',
              headerName: 'Member ID',
              width: 157,
              sortable: false
            },
            {
              field: 'archivedBy',
              headerName: 'Archived',
              width: 336,
              sortable: false,
              renderCell: ({ row }) => (
                <Box height="100%" display="flex" flexDirection="column" justifyContent="center">
                  <Typography variant="body2" color="textPrimary">{getArchivedAtDisplay(row.archivedAt!)} by {row.archivedByFullName}</Typography>
                  <Typography variant="body2">{row.archiveReason && getArchiveReasonLabel(row.archiveReason)}</Typography>
                </Box>
              ),
            },
            {
              field: 'id',
              headerName: '',
              width: 80,
              align: 'center',
              sortable: false,
              renderCell: ({ row }) => (
                <ArchivedInsuranceOptions
                  insuranceData={row}
                  restoreDisabledReason={restoreDisabledReason}
                  onRestoreInsurance={(archivedInsurance, benefits) => restoreMutation.mutateAsync({ archivedInsurance, benefits })}
                  isRestoreAsSecondary={isRestoreAsSecondary}
                />
              )
            }
          ]}
          classes={classes}
          slots={{ footer: () => null }}
          disableColumnFilter
          disableColumnSorting
          disableColumnMenu
          disableRowSelectionOnClick
          disableColumnResize
        />
      </Paper>
    </Box>
  );
};

type ArchivedInsuranceOptionsProps = {
  insuranceData: InsuranceDataType;
  restoreDisabledReason: string | null;
  onRestoreInsurance: (archivedInsurance: InsuranceDataType, benefits: InsuranceBenefitsFormType) => Promise<void>;
  restoreLoading?: boolean;
  isRestoreAsSecondary?: boolean;
};

const ArchivedInsuranceOptions: React.FC<ArchivedInsuranceOptionsProps> = (props) => {
  const [ modalOpen, setModalOpen ] = useState<'details' | 'restore' | null>(null);

  return (
    <div>
      <ArchivedInsuranceDetailsModal
        open={modalOpen === 'details'}
        onClose={() => setModalOpen(null)}
        insuranceDetails={props.insuranceData}
        onOpenRestoreModal={() => setModalOpen('restore')}
        restoreDisabledReason={props.restoreDisabledReason}
      />
      <ArchivedInsuranceRestoreModal
        open={modalOpen === 'restore'}
        onClose={() => setModalOpen(null)}
        insuranceData={props.insuranceData}
        onRestoreInsurance={(benefits) => props.onRestoreInsurance(props.insuranceData, benefits)}
        isRestoreAsSecondary={props.isRestoreAsSecondary}
      />
      <MoreOptionsDropdown>
        <MoreOptionsDropdownItem onClick={() => setModalOpen('details')}>
          View Details
        </MoreOptionsDropdownItem>
        <Divider />

        <Tooltip arrow={false} placement="bottom" title={props.restoreDisabledReason ?? null}>
          <div>
            <MoreOptionsDropdownItem
              disabled={!!props.restoreDisabledReason}
              onClick={() => setModalOpen('restore')}
            >
              Restore Insurance
            </MoreOptionsDropdownItem>
          </div>
        </Tooltip>
      </MoreOptionsDropdown>
    </div>
  );
};


const useStyles = makeStyles()((theme) => ({
  root: {
    boxShadow: 'none',
    border: 'none',
    '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
      outline: 'none !important',
    },
    '& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus':
    {
      outline: 'none !important',
    },
  },
  columnHeaderTitle: {
    fontWeight: 450,
    color: theme.palette.text.primary
  },
  columnSeparator: {
    display: 'none !important',
  }
}));
